import { cx } from "@/utils/classnames";
import { useVirtualizer, useWindowVirtualizer } from "@tanstack/react-virtual";
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

const SCROLLABLE_OVERFLOW_VALUES = new Set(["auto", "scroll", "overlay"]);

const getNearestVerticalScrollElement = (element: HTMLElement | null): HTMLElement | null => {
  if (typeof window === "undefined" || element == null) {
    return null;
  }
  let parent = element.parentElement;
  while (parent != null && parent !== document.body && parent !== document.documentElement) {
    if (SCROLLABLE_OVERFLOW_VALUES.has(window.getComputedStyle(parent).overflowY)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

const getScrollMargin = (anchor: HTMLElement | null, scrollElement: HTMLElement | null): number => {
  if (typeof window === "undefined" || anchor == null) {
    return 0;
  }
  if (scrollElement == null) {
    return anchor.getBoundingClientRect().top + window.scrollY;
  }
  return (
    anchor.getBoundingClientRect().top -
    scrollElement.getBoundingClientRect().top +
    scrollElement.scrollTop
  );
};

// Firefox's getBoundingClientRect can fight the virtualizer's transform measurement.
//
// This picks *which* measurer runs, not whether one does. `measureElement:
// undefined` is dropped by the virtualizer's option merge, leaving its built-in
// ResizeObserver measurer in place, so Firefox still measures every mounted row
// and variable-height items work there. The override below only exists to floor a
// zero reading at the estimate.
const canMeasureElement =
  typeof window !== "undefined" && navigator.userAgent.indexOf("Firefox") === -1;

export interface VirtualizedListProps<T> {
  items: readonly T[];
  getKey: (item: T, index: number) => string;
  renderItem: (item: T, index: number) => ReactNode;
  getItemClassName?: (item: T, index: number) => string | undefined;
  estimatedItemHeight?: number;
  overscan?: number;
  className?: string;
  /** Known scroll owner for layouts where computed-style discovery is unreliable, such as portals. */
  scrollElementRef?: React.RefObject<HTMLElement | null>;
  /** Index to center in view once on mount, e.g. a menu's selected option. */
  initialScrollIndex?: number;
  /** Index to keep visible as it changes, e.g. a menu's keyboard-active option. */
  scrollToIndex?: number;
}

/**
 * A list that window/scroll-parent virtualizes variable-height items (measured once mounted), so
 * pages with very long lists only mount what's visible. Items are keyed stably, so background
 * refetches that replace the array don't re-measure or jump rows.
 */
export function VirtualizedList<T>({
  items,
  getKey,
  renderItem,
  getItemClassName,
  estimatedItemHeight = 44,
  overscan = 8,
  className,
  scrollElementRef,
  initialScrollIndex,
  scrollToIndex,
}: VirtualizedListProps<T>) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  const updateScrollMetrics = useCallback(() => {
    const nextScrollElement =
      scrollElementRef?.current ?? getNearestVerticalScrollElement(anchorRef.current);
    setScrollElement(nextScrollElement);
    setScrollMargin(getScrollMargin(anchorRef.current, nextScrollElement));
  }, [scrollElementRef]);

  useEffect(() => {
    updateScrollMetrics();
    window.addEventListener("resize", updateScrollMetrics);
    const resizeObserver =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateScrollMetrics) : null;
    if (resizeObserver != null) {
      if (anchorRef.current != null) resizeObserver.observe(anchorRef.current);
      if (scrollElement != null) resizeObserver.observe(scrollElement);
    }
    return () => {
      window.removeEventListener("resize", updateScrollMetrics);
      resizeObserver?.disconnect();
    };
  }, [scrollElement, updateScrollMetrics]);

  const opts = useMemo(
    () => ({
      count: items.length,
      estimateSize: () => estimatedItemHeight,
      overscan,
      measureElement: canMeasureElement
        ? (element: Element) => {
            const measured = element.getBoundingClientRect().height;
            return measured > 0 ? measured : estimatedItemHeight;
          }
        : undefined,
      // Stable key so background refetches reuse measured heights instead of jumping the rows.
      getItemKey: (index: number) => getKey(items[index], index),
    }),
    [items, estimatedItemHeight, overscan, getKey],
  );

  const windowVirtualizer = useWindowVirtualizer({
    ...opts,
    enabled: scrollElementRef == null && scrollElement == null,
    scrollMargin,
  });
  const elementVirtualizer = useVirtualizer({
    ...opts,
    enabled: scrollElement != null,
    getScrollElement: () => scrollElement,
    scrollMargin,
  });
  const virtualizer = scrollElement == null ? windowVirtualizer : elementVirtualizer;

  // Applied once, and only after the nearest scroll container is measured:
  // before that the window virtualizer is active and scrollToIndex would
  // scroll the page instead of the list's own container.
  const appliedInitialScrollRef = useRef(false);
  useEffect(() => {
    if (initialScrollIndex == null || appliedInitialScrollRef.current || scrollElement == null) {
      return;
    }
    appliedInitialScrollRef.current = true;
    virtualizer.scrollToIndex(initialScrollIndex, { align: "center" });
  }, [initialScrollIndex, scrollElement, virtualizer]);

  useEffect(() => {
    if (scrollToIndex == null || scrollElement == null) {
      return;
    }
    virtualizer.scrollToIndex(scrollToIndex, { align: "auto" });
  }, [scrollElement, scrollToIndex, virtualizer]);

  return (
    <div
      ref={anchorRef}
      // The virtualizer adjusts scroll itself when a measured row changes size; browser
      // scroll anchoring would compete with it and jump the list.
      className={cx("relative w-full [overflow-anchor:none]", className)}
      style={{ height: virtualizer.getTotalSize() }}
    >
      {virtualizer.getVirtualItems().map((virtualItem) => {
        const item = items[virtualItem.index];
        return (
          <div
            key={getKey(item, virtualItem.index)}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            className={cx(
              "absolute left-0 top-0 w-full",
              getItemClassName?.(item, virtualItem.index),
            )}
            style={{ transform: `translateY(${virtualItem.start - scrollMargin}px)` }}
          >
            {renderItem(item, virtualItem.index)}
          </div>
        );
      })}
    </div>
  );
}
